import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import googlePlay from "../assets/google_play.png";
import applePlay from "../assets/apple_store_download-cutout.png";
import {
  ShieldCheck,
  LineChart,
  Lock,
  Wallet,
  TrendingUp,
  Heart,
  ArrowRight,
  PiggyBank,
  Target,
  Calculator,
  LayoutDashboard,
  CalendarClock,
  AlertTriangle,
  BrainCircuit,
  Users,
  FileText,
  BadgeIndianRupee,
  Building2,
  Coins,
  Landmark,
  Car,
  Gem,
  Bitcoin,
  CreditCard,
  Home,
  Briefcase,
  TreePine,
  BarChart3,
  Bell,
  CheckCircle2,
  Star,
  Globe,
  BookOpen,
  Clock,
  Layers,
  AreaChart,
  SlidersHorizontal,
  Inbox,
  Smartphone,
} from "lucide-react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { CALCULATOR_LIST } from "../components/calculators/calculators";
import { api, fmtINR } from "../lib/api";
import { toast } from "sonner";
import LazyAreaChart from "../components/charts/LazyAreaChart";
import LazyPieChart from "../components/charts/LazyPieChart";

// ─── Constants ────────────────────────────────────────────────────────────────

const SITE_URL = "https://www.accesswealth.in";

const HERO_GROWTH = Array.from({ length: 11 }).map((_, i) => ({
  year: 2025 + i,
  value: Math.round(1500000 * 1.13 ** i),
  invested: 1500000 + i * 600000,
}));

const ALLOC = [
  { name: "Equity", value: 45 },
  { name: "Debt", value: 22 },
  { name: "Real Estate", value: 18 },
  { name: "Gold", value: 8 },
  { name: "Cash", value: 7 },
];
const COLORS = ["#10b981", "#1e3a8a", "#f59e0b", "#a855f7", "#64748b"];

const ASSET_TYPES = [
  { icon: BarChart3, label: "Mutual Funds" },
  { icon: TrendingUp, label: "Stocks" },
  { icon: Landmark, label: "Fixed Deposits" },
  { icon: Coins, label: "Gold & Silver" },
  { icon: Building2, label: "Real Estate" },
  { icon: Bitcoin, label: "Crypto" },
  { icon: Briefcase, label: "EPF / PPF" },
  { icon: TreePine, label: "NPS" },
  { icon: Car, label: "Vehicles" },
  { icon: Globe, label: "Foreign Assets" },
  { icon: Gem, label: "Collectibles" },
  { icon: BookOpen, label: "Bonds" },
  { icon: Home, label: "REITs" },
  { icon: CreditCard, label: "Cash & Savings" },
  { icon: Layers, label: "Unlisted Equity" },
  { icon: PiggyBank, label: "Chit Funds" },
  { icon: Inbox, label: "Other Assets" },
];

const PAIN_POINTS = [
  { icon: "📱", label: "Bank apps" },
  { icon: "📊", label: "Spreadsheets" },
  { icon: "📄", label: "PDF statements" },
  { icon: "📧", label: "Email trails" },
  { icon: "🏦", label: "Brokers & agents" },
  { icon: "🗂️", label: "Physical files" },
  { icon: "💬", label: "Family conversations" },
  { icon: "🧠", label: "Memory" },
];

const WHO_FOR = [
  {
    emoji: "👨‍💼",
    title: "Working Professionals",
    desc: "You've been investing across multiple platforms for years. It's time to see the full picture in one dashboard.",
  },
  {
    emoji: "👨‍👩‍👧‍👦",
    title: "Families",
    desc: "Ensure your spouse and children know exactly what you own and where to find it when it matters most.",
  },
  {
    emoji: "📈",
    title: "Long-term Investors",
    desc: "Track net worth trajectory, run scenarios, and understand whether you're truly on track for your goals.",
  },
  {
    emoji: "🏢",
    title: "Business Owners",
    desc: "Separate business and personal wealth clearly. Track both without them bleeding into each other.",
  },
  {
    emoji: "🔐",
    title: "Inheritance Planners",
    desc: "Assign nominees, allocate assets, and leave a clear, secure record for the people who matter.",
  },
];

const VAULT_FEATURES = [
  {
    icon: Lock,
    title: "AES-256 encrypted",
    desc: "Every document end-to-end encrypted. Even we can't read your files.",
  },
  {
    icon: Users,
    title: "Tiered nominee access",
    desc: "Trustees, executors and nominees each get exactly the right level of access.",
  },
  {
    icon: FileText,
    title: "Document vault",
    desc: "Will, insurance, property deeds, crypto seeds — all in one secure place.",
  },
  {
    icon: Bell,
    title: "Dead-man switch",
    desc: "Inactivity-based access release ensures your family is never locked out.",
  },
  {
    icon: ShieldCheck,
    title: "DPDP ready",
    desc: "Built to comply with India's Digital Personal Data Protection Act.",
  },
  {
    icon: Clock,
    title: "Time-locked access",
    desc: "Set conditions for when and how your data becomes visible to nominees.",
  },
];

const LIABILITIES = [
  { label: "Home Loan", emi: "₹42,000/mo", due: "5 days", status: "upcoming" },
  { label: "Car Loan", emi: "₹12,500/mo", due: "12 days", status: "ok" },
  { label: "Personal Loan", emi: "₹8,200/mo", due: "18 days", status: "ok" },
  { label: "Credit Card", emi: "₹6,400 due", due: "2 days", status: "urgent" },
];

const FAQ_ITEMS = [
  {
    q: "How is AccessWealth different from a portfolio tracker?",
    a: "Portfolio trackers show your investment returns. AccessWealth shows your entire financial life — assets across 17 categories, liabilities with EMI tracking, goals with timelines, net worth projections, and family continuity. It's the difference between a speedometer and a full cockpit.",
  },
  {
    q: "Can I track liabilities like loans and EMIs?",
    a: "Yes — and this is one of our strongest features. Add home loans, car loans, personal loans, credit cards. AccessWealth tracks outstanding amounts, monthly EMIs, due dates, and calculates your true net worth (assets minus liabilities) automatically.",
  },
  {
    q: "What asset types can I track?",
    a: "17 types: mutual funds, stocks, fixed deposits, gold, real estate, crypto, EPF/PPF, NPS, vehicles, foreign assets, collectibles, bonds, REITs, cash, unlisted equity, chit funds, and more. If you own it, you can track it.",
  },
  {
    q: "How do the wealth projections work?",
    a: "You input your current portfolio and expected contribution rate. AccessWealth projects your net worth across three scenarios — conservative (6%), moderate (10%), and aggressive (14%) — over your lifetime, adjusted for inflation. You can also model specific goals like retirement or FIRE.",
  },
  {
    q: "Is my financial data safe on AccessWealth?",
    a: "All data is encrypted at rest (AES-256) and in transit (TLS 1.3). Vault documents use zero-trust access — even our engineers can't read your files. We're built to comply with India's DPDP Act.",
  },
  {
    q: "Does AccessWealth connect to my bank automatically?",
    a: "Account Aggregator (RBI) integration is coming soon. For now, you add holdings manually — which most users say actually improves their financial awareness significantly.",
  },
  {
    q: "Can I cancel my premium plan anytime?",
    a: "Yes, one click from settings. Your data stays with you forever — we never delete records when you downgrade.",
  },
];

// ─── JSON-LD schemas ──────────────────────────────────────────────────────────

const JSON_LD_WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "AccessWealth",
  url: SITE_URL,
  description:
    "Personal wealth management platform for Indian households. Track assets, liabilities, goals and protect your family's financial future.",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/calculators?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

const JSON_LD_ORG = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AccessWealth",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: [],
  contactPoint: {
    "@type": "ContactPoint",
    email: "info@accesswealth.in",
    contactType: "customer support",
    availableLanguage: ["English", "Hindi"],
  },
};

const JSON_LD_SOFTWARE = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "AccessWealth",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web, iOS, Android",
  url: SITE_URL,
  description:
    "Track your net worth, manage EMIs, plan financial goals, and protect your family's wealth — all in one secure platform.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "INR",
    description: "Free plan available. Premium plans starting from ₹299/month.",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "240",
  },
};

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Landing() {
  return (
    <>
      {/* ── SEO Head ── */}
      <Helmet>
        <html lang="en" />
        <title>
          AccessWealth — Personal Wealth Management for Indian Households
        </title>
        <meta
          name="description"
          content="Track your net worth, manage EMIs & loans, plan financial goals, and protect your family's wealth. 17 asset types, 25+ calculators, AES-256 encrypted. Free to start."
        />
        <meta
          name="keywords"
          content="personal wealth management India, net worth tracker, EMI tracker, financial goal planner, investment tracker India, family wealth planning, mutual fund tracker, loan EMI calculator"
        />
        <link rel="canonical" href={SITE_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:site_name" content="AccessWealth" />
        <meta
          property="og:title"
          content="AccessWealth — Your Personal Financial Command Center"
        />
        <meta
          property="og:description"
          content="Stop worrying about money. Track every asset, every loan, every goal. Know your true net worth — always. Free to start."
        />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@accesswealth" />
        <meta
          name="twitter:title"
          content="AccessWealth — Personal Wealth Management for Indian Households"
        />
        <meta
          name="twitter:description"
          content="Track net worth, EMIs, goals and protect family wealth. 17 asset types. 25+ calculators. Free to start."
        />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        {/* Technical */}
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta name="author" content="AccessWealth" />
        <meta name="theme-color" content="#059669" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />

        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://images.unsplash.com" />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(JSON_LD_WEBSITE)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(JSON_LD_ORG)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(JSON_LD_SOFTWARE)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(JSON_LD_FAQ)}
        </script>
      </Helmet>

      <div className="antialiased">
        <Navbar />
        <main id="main-content">
          <Hero />
          <ProblemSection />
          <NetWorthSection />
          <WhoIsItFor />
          <AssetCoverage />
          <LiabilitySection />
          <ProjectionsSection />
          <CalculatorsPreview />
          <VaultSection />
          <Testimonials />
          <FAQ />
          <ContactSection />
          <CTA />
        </main>
        <Footer />
      </div>
    </>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section
      aria-label="Hero — AccessWealth personal wealth management"
      className="relative mesh-bg overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            {/* ONE true H1 per page — keyword-rich */}
            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 dark:text-white leading-[1.05]"
            >
              Stop worrying
              <br />
              about money.
              <br />
              <span className="gradient-text">Start building wealth.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed">
              AccessWealth is your personal financial command center. Track
              every asset, every liability, every goal — and always know exactly
              where you stand and where you're headed.
            </p>

            <ul
              aria-label="Key features"
              className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 list-none p-0"
            >
              {[
                {
                  icon: Wallet,
                  label: "Track all your assets",
                  sub: "17 categories",
                },
                {
                  icon: CreditCard,
                  label: "Manage every liability",
                  sub: "EMIs, loans, cards",
                },
                {
                  icon: TrendingUp,
                  label: "See your future wealth",
                  sub: "25-year projections",
                },
              ].map(({ icon: Icon, label, sub }) => (
                <li
                  key={label}
                  className="flex items-start gap-3 rounded-xl bg-white/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 px-4 py-3"
                >
                  <div
                    className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 flex-shrink-0"
                    aria-hidden="true"
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-200">
                      {label}
                    </span>
                    <span className="block text-[11px] text-slate-500">
                      {sub}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register" className="btn-primary">
                Start free
              </Link>

              <Link to="/calculators" className="btn-secondary">
                Try calculators
              </Link>
            </div>

            <div className="mt-5 flex items-center gap-4 text-sm text-slate-500">
              <a
                href="https://play.google.com/store/apps/details?id=com.nfai.accesswealth&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={googlePlay}
                  alt="Get it on Google Play"
                  className="h-14 w-auto cursor-pointer"
                />
              </a>
              <a
                href="https://apps.apple.com/in/app/accesswealth/id6774110097"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={applePlay}
                  alt="Get it on Apple Store"
                  className="h-11 w-auto cursor-pointer"
                />
              </a>
            </div>
          </div>

          <div className="lg:col-span-6" aria-hidden="true">
            {/* aria-hidden: decorative preview, not primary content */}
            <HeroDashboardPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroDashboardPreview() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-emerald-500/20 via-transparent to-blue-600/15 blur-2xl rounded-3xl -z-10" />
      <div className="card-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-slate-500">Total Net Worth</p>
            <p className="num-mono text-3xl font-bold text-slate-900 dark:text-white">
              {fmtINR(9026000)}
            </p>
            <p className="mt-1 text-xs text-emerald-600 font-semibold flex items-center gap-1">
              <TrendingUp className="h-3 w-3" aria-hidden="true" /> +18.4% YoY
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-wider bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-full font-semibold">
            Live preview
          </span>
        </div>

        {/* Chart skeleton shown until lazy chunk loads */}
        <div className="w-full h-44 min-w-0">
          <LazyAreaChart data={HERO_GROWTH} />
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <MiniCard
            icon={Wallet}
            label="Assets"
            value={fmtINR(13200000)}
            accent="text-slate-900 dark:text-white"
          />
          <MiniCard
            icon={CreditCard}
            label="Liabilities"
            value={fmtINR(4174000)}
            accent="text-red-500"
          />
          <MiniCard
            icon={Target}
            label="Health"
            value="82 / 100"
            accent="text-emerald-600"
          />
        </div>
      </div>

      <div className="hidden md:block absolute -bottom-6 -left-6 card-soft p-4 w-56">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          Allocation
        </p>
        <div className="flex items-center gap-3">
          <div className="h-20 w-20 min-w-[80px] min-h-[80px]">
            <LazyPieChart data={ALLOC} colors={COLORS} />
          </div>
          <ul className="grid gap-1 list-none p-0">
            {ALLOC.slice(0, 4).map((a, i) => (
              <li key={a.name} className="flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: COLORS[i] }}
                  aria-hidden="true"
                />
                <span className="text-[10px] text-slate-600 dark:text-slate-300">
                  {a.name}
                </span>
                <span className="text-[10px] num-mono ml-auto text-slate-500">
                  {a.value}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─── Problem Section ──────────────────────────────────────────────────────────

function ProblemSection() {
  return (
    <section
      aria-labelledby="problem-heading"
      className="border-y border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-rose-500 mb-3">
            The Problem
          </p>
          <h2
            id="problem-heading"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white max-w-2xl mx-auto"
          >
            Your financial life is scattered across 8+ places.
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
            When everything is fragmented, you never have the full picture. And
            neither will your family when they need it most.
          </p>
        </div>

        <ul
          aria-label="Fragmented financial data sources"
          className="flex flex-wrap justify-center gap-3 mb-10 list-none p-0"
        >
          {PAIN_POINTS.map((p) => (
            <li
              key={p.label}
              className="flex items-center gap-2 rounded-full bg-white dark:bg-slate-900 border border-rose-100 dark:border-rose-500/20 px-4 py-2 shadow-sm"
            >
              <span aria-hidden="true">{p.icon}</span>
              <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                {p.label}
              </span>
            </li>
          ))}
        </ul>

        <div
          className="flex items-center justify-center gap-4"
          aria-hidden="true"
        >
          <div className="h-px flex-1 max-w-32 bg-slate-200 dark:bg-slate-800" />
          <p className="flex items-center gap-2 text-emerald-600 font-semibold text-sm">
            <ArrowRight className="h-4 w-4" />
            AccessWealth centralises everything into one secure system
          </p>
          <div className="h-px flex-1 max-w-32 bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    </section>
  );
}

// ─── Net Worth Section ────────────────────────────────────────────────────────

function NetWorthSection() {
  return (
    <section
      id="features"
      aria-labelledby="networth-heading"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20"
    >
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            Your Financial Dashboard
          </p>
          <h2
            id="networth-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Know your real
            <br />
            <span className="gradient-text">net worth. Always.</span>
          </h2>
          <p className="mt-5 text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Most people have a vague sense of what they own. AccessWealth gives
            you the exact number — assets minus liabilities — updated every time
            you log in. No spreadsheets. No guesswork.
          </p>
          <ul className="mt-8 space-y-4 list-none p-0">
            {[
              {
                icon: LayoutDashboard,
                title: "One living dashboard",
                desc: "Net worth, allocation breakdown, health score, and goal progress — all visible at a glance.",
              },
              {
                icon: AreaChart,
                title: "Wealth health score",
                desc: "A 0–100 score that tells you how balanced, diversified, and goal-aligned your portfolio is.",
              },
              {
                icon: SlidersHorizontal,
                title: "Full asset allocation view",
                desc: "See exactly how your wealth is spread across equity, debt, real estate, gold, and more.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex gap-4">
                <div
                  className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                    {title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-4" aria-hidden="true">
          <div className="card-soft p-6">
            <p className="text-xs text-slate-500 mb-1">True Net Worth</p>
            <div className="flex items-end gap-2 mb-5">
              <span className="num-mono text-4xl font-bold text-slate-900 dark:text-white">
                {fmtINR(9026000)}
              </span>
              <span className="text-xs text-emerald-600 font-semibold mb-1.5 flex items-center gap-0.5">
                <TrendingUp className="h-3 w-3" /> +18.4%
              </span>
            </div>
            <div
              className="flex rounded-full overflow-hidden h-2.5 mb-4"
              role="img"
              aria-label="Asset allocation bar: 45% Equity, 22% Debt, 18% Real Estate, 8% Gold, 7% Cash"
            >
              {ALLOC.map((a, i) => (
                <div
                  key={a.name}
                  style={{ width: `${a.value}%`, background: COLORS[i] }}
                />
              ))}
            </div>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 list-none p-0">
              {ALLOC.map((a, i) => (
                <li key={a.name} className="flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: COLORS[i] }}
                  />
                  <span className="text-xs text-slate-600 dark:text-slate-400">
                    {a.name}
                  </span>
                  <span className="text-xs num-mono text-slate-500">
                    {a.value}%
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                  Total Assets
                </p>
                <p className="num-mono font-bold text-slate-900 dark:text-white">
                  {fmtINR(13200000)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                  Total Liabilities
                </p>
                <p className="num-mono font-bold text-red-500">
                  {fmtINR(4174000)}
                </p>
              </div>
            </div>
          </div>

          <div className="card-soft p-5 flex items-center gap-5">
            <svg
              viewBox="0 0 36 36"
              className="h-16 w-16 -rotate-90 flex-shrink-0"
              role="img"
              aria-label="Wealth health score: 82 out of 100"
            >
              <circle
                cx="18"
                cy="18"
                r="15.9"
                fill="none"
                stroke="rgba(16,185,129,0.12)"
                strokeWidth="3"
              />
              <circle
                cx="18"
                cy="18"
                r="15.9"
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
                strokeDasharray="82 100"
                strokeLinecap="round"
              />
              <text
                x="18"
                y="22"
                textAnchor="middle"
                fontSize="8"
                fill="#059669"
                className="rotate-90 origin-center"
                style={{
                  transform: "rotate(90deg)",
                  transformOrigin: "18px 18px",
                }}
              >
                82
              </text>
            </svg>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                Wealth Health Score
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Good diversification. Consider increasing your emergency fund to
                push past 85.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Who Is It For ────────────────────────────────────────────────────────────

function WhoIsItFor() {
  return (
    <section
      aria-labelledby="who-heading"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20"
    >
      <div className="text-center mb-12">
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
          Built For
        </p>
        <h2
          id="who-heading"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
        >
          Who is AccessWealth for?
        </h2>
        <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
          Anyone who wants financial clarity — not just for themselves, but for
          the people they love.
        </p>
      </div>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 list-none p-0">
        {WHO_FOR.map((w) => (
          <li
            key={w.title}
            className="card-soft p-6 group hover:border-emerald-200 dark:hover:border-emerald-500/30 transition-colors"
          >
            <span className="text-3xl mb-4 block" aria-hidden="true">
              {w.emoji}
            </span>
            <h3 className="font-display font-bold text-slate-900 dark:text-white text-lg mb-2">
              {w.title}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              {w.desc}
            </p>
          </li>
        ))}
        <li className="card-soft p-6 bg-emerald-50 dark:bg-emerald-500/5 border-emerald-200 dark:border-emerald-500/20 flex flex-col items-start justify-between">
          <div>
            <span className="text-3xl mb-4 block" aria-hidden="true">
              ✨
            </span>
            <h3 className="font-display font-bold text-slate-900 dark:text-white text-lg mb-2">
              That's you, isn't it?
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Start for free. No credit card. Pre-loaded with sample data so you
              can explore right away.
            </p>
          </div>
          <Link to="/register" className="mt-6 btn-primary text-sm">
            Get started free{" "}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </li>
      </ul>
    </section>
  );
}

// ─── Asset Coverage ───────────────────────────────────────────────────────────

function AssetCoverage() {
  return (
    <section
      aria-labelledby="assets-heading"
      className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
              17 Asset Types
            </p>
            <h2
              id="assets-heading"
              className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
            >
              If you own it,
              <br />
              <span className="gradient-text">you can track it.</span>
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300 leading-relaxed">
              From mainstream investments to niche assets — mutual funds,
              stocks, gold, real estate, crypto, chit funds, and everything in
              between. Plus liabilities like loans, EMIs, and credit cards, so
              you always know your true net worth.
            </p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Assets — 17 types
              </span>
              <div
                className="h-4 w-px bg-slate-300 dark:bg-slate-700"
                aria-hidden="true"
              />
              <span className="flex items-center gap-2 text-sm font-semibold text-red-500">
                <CreditCard className="h-4 w-4" aria-hidden="true" />
                Liabilities — loans, EMIs, mortgages
              </span>
            </div>
            {/* Net worth equation — keyword-rich copy */}
            <div className="mt-6 p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 inline-block">
              <p className="text-xs text-slate-500 mb-1">
                True Net Worth Formula
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] text-slate-400">Total Assets</p>
                  <p className="num-mono font-bold text-slate-900 dark:text-white">
                    {fmtINR(13200000)}
                  </p>
                </div>
                <span className="text-slate-400 font-bold" aria-hidden="true">
                  −
                </span>
                <div>
                  <p className="text-[10px] text-slate-400">
                    Total Liabilities
                  </p>
                  <p className="num-mono font-bold text-red-500">
                    {fmtINR(4174000)}
                  </p>
                </div>
                <span className="text-slate-400 font-bold" aria-hidden="true">
                  =
                </span>
                <div>
                  <p className="text-[10px] text-slate-400">Net Worth</p>
                  <p className="num-mono font-bold text-emerald-600">
                    {fmtINR(9026000)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <ul
            aria-label="17 supported asset types in AccessWealth"
            className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3 list-none p-0"
          >
            {ASSET_TYPES.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="card-soft p-3 flex flex-col items-center gap-2 text-center group hover:border-emerald-200 dark:hover:border-emerald-500/30 transition-colors"
              >
                <div
                  className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 group-hover:bg-emerald-100 transition-colors"
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </div>
                <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300 leading-tight">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Liability Section ────────────────────────────────────────────────────────

function LiabilitySection() {
  return (
    <section
      aria-labelledby="liability-heading"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        {/* Decorative UI mockup */}
        <div className="space-y-3 order-2 lg:order-1" aria-hidden="true">
          <div className="card-soft p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">
                  Total Outstanding
                </p>
                <p className="num-mono text-2xl font-bold text-slate-900 dark:text-white mt-0.5">
                  {fmtINR(4174000)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-widest text-slate-500">
                  Monthly EMIs
                </p>
                <p className="num-mono text-2xl font-bold text-red-500 mt-0.5">
                  {fmtINR(69100)}
                </p>
              </div>
            </div>
            <ul className="space-y-2 list-none p-0">
              {LIABILITIES.map((l) => (
                <li
                  key={l.label}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                    l.status === "urgent"
                      ? "bg-red-50 dark:bg-red-500/5 border-red-200 dark:border-red-500/20"
                      : l.status === "upcoming"
                        ? "bg-amber-50 dark:bg-amber-500/5 border-amber-200 dark:border-amber-500/20"
                        : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      l.status === "urgent"
                        ? "bg-red-500"
                        : l.status === "upcoming"
                          ? "bg-amber-500"
                          : "bg-emerald-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {l.label}
                    </p>
                    <p className="text-xs text-slate-500">{l.emi}</p>
                  </div>
                  <span
                    className={`text-xs font-semibold flex items-center gap-1 ${
                      l.status === "urgent"
                        ? "text-red-600"
                        : l.status === "upcoming"
                          ? "text-amber-600"
                          : "text-slate-500"
                    }`}
                  >
                    {l.status === "urgent" && (
                      <AlertTriangle className="h-3 w-3" />
                    )}
                    <CalendarClock className="h-3 w-3" />
                    Due in {l.due}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="card-soft p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Debt-to-Asset Ratio</p>
              <p className="num-mono text-xl font-bold text-amber-600">31.6%</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Healthy range: below 40%
              </p>
            </div>
            <div className="card-soft p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Projected Debt-free</p>
              <p className="num-mono text-xl font-bold text-emerald-600">
                Mar 2031
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                At current repayment pace
              </p>
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <p className="text-xs font-bold uppercase tracking-widest text-red-500 mb-3">
            Liability Tracking
          </p>
          <h2
            id="liability-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Never miss an EMI.
            <br />
            <span className="gradient-text">Know when you'll be free.</span>
          </h2>
          <p className="mt-5 text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Debt is part of wealth building. AccessWealth helps you track every
            loan, every EMI due date, and your debt-to-asset ratio — so you stay
            on top of obligations without letting them sneak up on you.
          </p>
          <ul className="mt-8 space-y-4 list-none p-0">
            {[
              {
                icon: CalendarClock,
                title: "EMI due date tracking",
                desc: "See every payment coming up this month, color-coded by urgency.",
              },
              {
                icon: BarChart3,
                title: "Outstanding balance overview",
                desc: "Total debt across all loans at a glance. Understand the full picture.",
              },
              {
                icon: BrainCircuit,
                title: "Debt-free date projection",
                desc: "Based on your current repayment, see exactly when you'll be clear of each loan.",
              },
              {
                icon: AlertTriangle,
                title: "Overdue payment alerts",
                desc: "Never get caught off guard. AccessWealth flags upcoming and overdue payments.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex gap-4">
                <div
                  className="grid h-9 w-9 place-items-center rounded-xl bg-red-50 dark:bg-red-500/10 text-red-500 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                    {title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Projections ──────────────────────────────────────────────────────────────

function ProjectionsSection() {
  const scenarios = [
    {
      label: "Conservative",
      rate: "6% p.a.",
      value: fmtINR(34200000),
      color: "#f59e0b",
    },
    {
      label: "Moderate",
      rate: "10% p.a.",
      value: fmtINR(63800000),
      color: "#10b981",
    },
    {
      label: "Aggressive",
      rate: "14% p.a.",
      value: fmtINR(118500000),
      color: "#3b82f6",
    },
  ];

  return (
    <section
      aria-labelledby="projections-heading"
      className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
              Wealth Projections
            </p>
            <h2
              id="projections-heading"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white"
            >
              See your wealth
              <br />
              <span className="gradient-text">at 50, 60, and 70.</span>
            </h2>
            <p className="mt-5 text-slate-600 dark:text-slate-300 text-base leading-relaxed">
              Stop guessing about retirement. Enter your current portfolio and
              contribution rate — AccessWealth shows you three honest
              projections over your lifetime, inflation-adjusted, so you know
              whether you're on track or need to change course.
            </p>
            <ul className="mt-8 space-y-3 list-none p-0">
              {scenarios.map((s) => (
                <li
                  key={s.label}
                  className="flex items-center gap-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-5 py-3.5"
                >
                  <span
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ background: s.color }}
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {s.label}
                    </p>
                    <p className="text-xs text-slate-500">
                      {s.rate} annual return
                    </p>
                  </div>
                  <p className="num-mono font-bold text-sm text-slate-900 dark:text-white">
                    {s.value}
                  </p>
                  <p className="text-xs text-slate-400">in 25 yrs</p>
                </li>
              ))}
            </ul>
            <ul
              aria-label="Projection features"
              className="mt-6 flex flex-wrap gap-3 text-xs text-slate-500 list-none p-0"
            >
              {[
                "Retirement planning",
                "FIRE calculator",
                "Goal timelines",
                "Inflation-adjusted",
              ].map((t) => (
                <li
                  key={t}
                  className="flex items-center gap-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-1"
                >
                  <CheckCircle2
                    className="h-3 w-3 text-emerald-500"
                    aria-hidden="true"
                  />{" "}
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="card-soft p-6" aria-hidden="true">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                25-Year Net Worth Projection
              </p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                Starting: {fmtINR(9026000)}
              </p>
            </div>
            <div className="h-64">
              <Suspense
                fallback={
                  <div className="h-64 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
                }
              >
                <LazyAreaChart data={HERO_GROWTH} showInvested />
              </Suspense>
            </div>
            <p className="mt-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 text-center">
              Pre-loaded with sample data — sign up to model your actual
              portfolio
            </p>
          </div>
        </div>

        {/* Goals sub-section */}
        <div className="mt-16 pt-16 border-t border-slate-200 dark:border-slate-800">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-3">
              Goal Planning
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter text-slate-900 dark:text-white">
              Turn ambitions into tracked milestones.
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-300 max-w-lg mx-auto text-sm">
              Create a goal, assign a target amount, link your assets to it, and
              let AccessWealth tell you when you'll get there — and what you
              need to contribute monthly to stay on track.
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 list-none p-0">
            {[
              {
                emoji: "🏠",
                label: "Home purchase",
                target: "₹80L",
                progress: 64,
                due: "3.2 years",
              },
              {
                emoji: "🎓",
                label: "Child's education",
                target: "₹25L",
                progress: 38,
                due: "8 years",
              },
              {
                emoji: "🏖️",
                label: "Early retirement",
                target: "₹3Cr",
                progress: 22,
                due: "18 years",
              },
              {
                emoji: "✈️",
                label: "World trip",
                target: "₹5L",
                progress: 81,
                due: "5 months",
              },
            ].map((g) => (
              <li key={g.label} className="card-soft p-5">
                <span className="text-2xl mb-3 block" aria-hidden="true">
                  {g.emoji}
                </span>
                <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                  {g.label}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Target: {g.target}
                </p>
                <div
                  className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden"
                  role="progressbar"
                  aria-valuenow={g.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${g.label} goal: ${g.progress}% complete`}
                >
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${g.progress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-semibold text-emerald-600">
                    {g.progress}% done
                  </span>
                  <span className="text-xs text-slate-400">{g.due} left</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Calculators ──────────────────────────────────────────────────────────────

function CalculatorsPreview() {
  const featured = CALCULATOR_LIST.slice(0, 8);
  return (
    <section
      id="calculators"
      aria-labelledby="calculators-heading"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div className="max-w-xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            25+ Free Calculators
          </p>
          <h2
            id="calculators-heading"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Every financial decision,
            <br />
            decoded.
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            SIP returns, EMI affordability, retirement corpus, FIRE number, debt
            payoff timeline, tax savings — all with interactive charts. No
            Excel, no CA needed for the basics.
          </p>
        </div>
        <Link
          to="/calculators"
          data-testid="see-all-calcs"
          className="btn-secondary flex-shrink-0"
        >
          See all 25+ calculators →
        </Link>
      </div>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 list-none p-0">
        {featured.map((c) => (
          <li key={c.slug}>
            <Link
              to={`/calculators/${c.slug}`}
              data-testid={`calc-card-${c.slug}`}
              className="card-soft p-5 group block h-full"
              aria-label={`${c.name} calculator — ${c.tagline}`}
            >
              <div
                className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white"
                aria-hidden="true"
              >
                <Calculator className="h-4 w-4" />
              </div>
              <h3 className="mt-4 font-display font-bold text-slate-900 dark:text-white">
                {c.name}
              </h3>
              <p className="mt-1 text-xs text-slate-500 line-clamp-2">
                {c.tagline}
              </p>
              <span className="mt-4 text-xs font-semibold text-emerald-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Try it <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── Vault Section ────────────────────────────────────────────────────────────

function VaultSection() {
  return (
    <section
      id="vault"
      aria-labelledby="vault-heading"
      className="relative overflow-hidden bg-slate-900 dark:bg-slate-950 text-white"
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 600px" }}
    >
      {/* Background image — loaded lazily via CSS, no LCP impact */}
      <div
        className="absolute inset-0 -z-0 opacity-30"
        role="img"
        aria-label="Family walking at sunset — representing wealth legacy and family continuity"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1648221350871-e3ae3c8d0f58?w=1400&q=75&auto=format&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/40" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-300 mb-5">
              <Heart className="h-3 w-3" aria-hidden="true" /> Family Continuity
              Vault
            </p>
            <h2
              id="vault-heading"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter"
            >
              If you're not around tomorrow,
              <br />
              <span className="text-emerald-400">
                will your family know what you owned?
              </span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Most families discover fragmented, incomplete financial records
              after a loss. AccessWealth's Family Continuity Vault lets you
              securely store your complete financial picture — and gives the
              right people access at the right time.
            </p>

            <div className="mt-8 p-5 rounded-xl bg-white/5 border border-white/10">
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                What you can store in the vault
              </p>
              <ul className="grid grid-cols-2 gap-2 list-none p-0">
                {[
                  "Will & testament",
                  "Life insurance policies",
                  "Property documents",
                  "Fixed deposit records",
                  "Crypto recovery phrases",
                  "Bank account details",
                  "Locker details",
                  "Investment folios",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm text-slate-300"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/register"
              data-testid="vault-cta"
              className="mt-8 inline-flex items-center gap-2 btn-primary"
            >
              Start your continuity plan{" "}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <ul className="grid sm:grid-cols-2 gap-4 list-none p-0">
            {VAULT_FEATURES.map(({ icon: Icon, title, desc }) => (
              <li
                key={title}
                className="rounded-xl bg-white/5 border border-white/10 p-5"
              >
                <Icon
                  className="h-5 w-5 text-emerald-400 mb-3"
                  aria-hidden="true"
                />
                <h3 className="font-semibold text-sm text-white mb-1">
                  {title}
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

function Testimonials() {
  const quotes = [
    {
      quote:
        "I had no idea what my actual net worth was until AccessWealth. Seeing everything in one number — including my loans — changed how I invest.",
      name: "Rahul M.",
      role: "Software Engineer, Pune",
    },
    {
      quote:
        "The EMI tracker alone is worth it. I used to dread month-end. Now I can see exactly what's due and when. No surprises.",
      name: "Priya S.",
      role: "Marketing Manager, Bengaluru",
    },
    {
      quote:
        "The 25-year projection made me increase my SIPs immediately. Seeing your future wealth in actual rupees is motivating in a way no article ever was.",
      name: "Vikram T.",
      role: "Finance Manager, Mumbai",
    },
  ];

  return (
    <section
      aria-labelledby="testimonials-heading"
      className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40"
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 400px" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            User Stories
          </p>
          <h2
            id="testimonials-heading"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Why people use AccessWealth.
          </h2>
        </div>
        <ul className="grid md:grid-cols-3 gap-6 list-none p-0">
          {quotes.map((q) => (
            <li key={q.name} className="card-soft p-6">
              <div className="flex gap-1 mb-4" aria-label="5 star rating">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <blockquote className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-5 italic">
                "{q.quote}"
              </blockquote>
              <footer>
                <cite className="not-italic">
                  <p className="font-semibold text-sm text-slate-900 dark:text-white">
                    {q.name}
                  </p>
                  <p className="text-xs text-slate-500">{q.role}</p>
                </cite>
              </footer>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

function FAQ() {
  return (
    <section
      id="faq"
      aria-labelledby="faq-heading"
      className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20"
    >
      <div className="text-center mb-10">
        <h2
          id="faq-heading"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
        >
          Frequently asked questions
        </h2>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Everything you need to know before you start.
        </p>
      </div>
      {/* details/summary = native accordion, no JS needed = good for CWV */}
      <div className="space-y-3">
        {FAQ_ITEMS.map((it, i) => (
          <details
            key={i}
            className="card-soft p-5 group"
            data-testid={`faq-item-${i}`}
          >
            <summary className="cursor-pointer flex items-center justify-between gap-4 text-sm font-semibold text-slate-900 dark:text-white list-none">
              {it.q}
              <span
                className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none flex-shrink-0"
                aria-hidden="true"
              >
                +
              </span>
            </summary>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              {it.a}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

// ─── CTA ──────────────────────────────────────────────────────────────────────

function CTA() {
  return (
    <section
      aria-labelledby="cta-heading"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12"
    >
      <div className="card-soft p-10 lg:p-16 relative overflow-hidden">
        <div
          className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative text-center max-w-2xl mx-auto">
          <BadgeIndianRupee
            className="h-10 w-10 text-emerald-600 mx-auto"
            aria-hidden="true"
          />
          <h2
            id="cta-heading"
            className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Stop worrying about money.
            <br />
            Start understanding it.
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            Start in 60 seconds. Pre-loaded with sample data so you can explore
            immediately. Free forever for core features.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/register"
              data-testid="bottom-cta-register"
              className="btn-primary"
            >
              Create my account — it's free
            </Link>
            <Link
              to="/login"
              data-testid="bottom-cta-login"
              className="btn-secondary"
            >
              Sign in
            </Link>
          </div>
          <ul
            aria-label="Trust signals"
            className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-500 list-none p-0"
          >
            {[
              "No credit card required",
              "Sample data pre-loaded",
              "AES-256 encrypted",
              "Cancel anytime",
            ].map((t) => (
              <li key={t} className="flex items-center gap-1.5">
                <CheckCircle2
                  className="h-3.5 w-3.5 text-emerald-500"
                  aria-hidden="true"
                />{" "}
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────────

function ContactSection() {
  const [fields, setFields] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setFields((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", fields);
      setFields({ name: "", email: "", message: "" });
      toast.success("Message sent successfully!");
    } catch {
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="border-t border-slate-200 dark:border-slate-800 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20"
    >
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            Contact
          </p>
          <div className="mb-8 rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              For Personal Finance Advisors & Chartered Accountants
            </p>

            <h3 className="mt-2 text-xl font-semibold text-slate-900 dark:text-white">
              Grow your practice with AccessWealth
            </h3>

            <p className="mt-3 text-slate-600 dark:text-slate-300">
              If you're a Personal Finance Advisor or Chartered Accountant,
              register your interest to get access to our advisor platform. Help
              clients organize their finances, monitor their financial journey
              with their permission, and provide more informed advice—all from
              one secure dashboard.
            </p>

            <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-300">
              <li>✓ Secure client-authorized financial access</li>
              <li>
                ✓ Unified view of assets, liabilities, insurance, and goals
              </li>
              <li>✓ Better client engagement and portfolio reviews</li>
              <li>
                ✓ Save time collecting financial information and documents
              </li>
            </ul>

            <p className="mt-4 text-sm font-medium text-emerald-700 dark:text-emerald-400">
              Simply fill out the contact form and mention that you're a
              Personal Finance Advisor or Chartered Accountant. Our team will
              get in touch to begin your onboarding.
            </p>
          </div>
          <h2
            id="contact-heading"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Questions, partnerships, or support?
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-lg">
            Reach out to the AccessWealth team — we respond within 24–48 hours.
          </p>
          <address className="mt-8 space-y-5 not-italic">
            {[
              {
                label: "General Support",
                value: "info@accesswealth.in",
                href: "mailto:info@accesswealth.in",
              },
              {
                label: "Security Issues",
                value: "info@accesswealth.in",
                href: "mailto:info@accesswealth.in",
              },
              {
                label: "Response Time",
                value: "Usually within 24–48 hours",
                href: null,
              },
            ].map((item) => (
              <div key={item.label}>
                <p className="text-xs uppercase tracking-widest text-slate-500">
                  {item.label}
                </p>
                {item.href ? (
                  <a
                    href={item.href}
                    className="mt-1 font-semibold text-slate-900 dark:text-white hover:text-emerald-600 transition-colors"
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="mt-1 font-semibold text-slate-900 dark:text-white">
                    {item.value}
                  </p>
                )}
              </div>
            ))}
          </address>
        </div>

        <div className="card-soft p-6">
          <form onSubmit={submit} noValidate aria-label="Contact AccessWealth">
            <fieldset className="space-y-4 border-none p-0 m-0">
              <legend className="sr-only">Contact form</legend>
              <div>
                <label
                  htmlFor="contact-name"
                  className="text-xs font-medium text-slate-700 dark:text-slate-300"
                >
                  Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={fields.name}
                  onChange={update("name")}
                  className="input-soft mt-1"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="text-xs font-medium text-slate-700 dark:text-slate-300"
                >
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={fields.email}
                  onChange={update("email")}
                  className="input-soft mt-1"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="contact-message"
                  className="text-xs font-medium text-slate-700 dark:text-slate-300"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  required
                  rows={5}
                  value={fields.message}
                  onChange={update("message")}
                  className="input-soft mt-1 resize-none"
                  placeholder="How can we help? If you're a Personal Finance Advisor or Chartered Accountant, tell us about your practice."
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? "Sending…" : "Send message"}
              </button>
            </fieldset>
          </form>
        </div>
      </div>
    </section>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function MiniCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider">
        <Icon className="h-3 w-3" aria-hidden="true" /> {label}
      </div>
      <p className={`num-mono text-sm font-bold mt-0.5 ${accent}`}>{value}</p>
    </div>
  );
}
